import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Grid,
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";

import StoreIcon from "@mui/icons-material/Store";
import SendIcon from "@mui/icons-material/Send";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";

import {
  createBranch,
  getBranches,
  updateBranch,
} from "../services/BranchService";

import BranchTable from "../components/Branch/BranchTable";
import { useAuth } from "../context/AuthContext";
import AppDialog from "../components/General/Dialog/AppDialog";
import ActionHeaderCard from "../components/General/ActionHeaderCard";
import { useToast } from "../context/ToastContext";

export default function Branches() {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();

  const canViewBranches = hasPermission("ver sucursales");
  const canCreateBranch = hasPermission("crear sucursal");
  const canUpdateBranch = hasPermission("actualizar sucursal");

  const emptyForm = useMemo(
    () => ({
      identification: "",
      name: "",
      alias: "",
      owner: "",
      address: "",
      email: "",
      phone: "",
      is_active: true,
    }),
    [],
  );

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [branchesList, setBranchesList] = useState([]);

  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    if (canViewBranches) fetchBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewBranches]);

  const fetchBranches = async () => {
    setLoading(true);
    try {
      const res = await getBranches();
      setBranchesList(res.data?.data || []);
    } catch {
      showToast("Error al cargar sucursales.", "error")
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const temp = {};

    temp.identification = form.identification ? "" : "Identificación requerida";
    temp.name = form.name ? "" : "Nombre requerido";

    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      temp.email = "Correo inválido";
    }

    setErrors(temp);
    return Object.values(temp).every((x) => x === "");
  };

  const resetForm = () => {
    setForm(emptyForm);
    setErrors({});    setEditMode(false);
    setEditId(null);
  };

  const openCreate = () => {
    if (!canCreateBranch) {
      showToast("No tienes permiso para crear sucursales.", "error")
      return;
    }
    resetForm();
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    resetForm();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const handleToggleActive = (e) => {
    setForm({ ...form, is_active: e.target.checked });
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (editMode && !canUpdateBranch) {
      showToast("No tienes permiso para actualizar sucursales.", "info")
      return;
    }

    if (!editMode && !canCreateBranch) {
      showToast("No tienes permiso para crear sucursales.", "info")
      return;
    }

    if (!validate()) return;

    setLoading(true);
    try {
      const response = editMode
        ? await updateBranch(editId, form)
        : await createBranch(form);

      if (response.data?.status === "success") {
        showToast(
          response.data.message || "Sucursal guardada correctamente.",
        "success")
        await fetchBranches();
        closeForm();
      } else if (response.data?.message) {
        showToast(response.data.message, "error")
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        const apiErrors = {};
        Object.keys(error.response.data.errors).forEach((key) => {
          apiErrors[key] = error.response.data.errors[key][0];
        });
        setErrors(apiErrors);
      } else if (error.response?.data?.message) {
        showToast(error.response.data.message, "error")
      } else {
        showToast("Ocurrió un error inesperado.", "error")
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (branch) => {
    if (!canUpdateBranch) {
      showToast("No tienes permiso para actualizar sucursales.", "error")
      return;
    }

    setForm({
      identification: branch.identification || "",
      name: branch.name || "",
      alias: branch.alias || "",
      owner: branch.owner || "",
      address: branch.address || "",
      email: branch.email || "",
      phone: branch.phone || "",
      is_active: branch.is_active ?? true,
    });

    setEditMode(true);
    setEditId(branch.id);
    setErrors({});    setFormOpen(true);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Grid container spacing={2}>

        <Grid size={{ xs: 12 }}>
          <ActionHeaderCard
            icon={<StoreIcon />}
            title="Sucursales"
            description="Administrar sucursales activas del sistema."
            action={
              canCreateBranch ? (
                <Button
                  onClick={openCreate}
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 800,
                    textTransform: "none",
                  }}
                >
                  Nueva sucursal
                </Button>
              ) : null
            }
          />
        </Grid>

        {canViewBranches && (
          <Grid size={{ xs: 12 }}>
            <BranchTable
              branches={branchesList}
              onEdit={handleEdit}
              canEdit={canUpdateBranch}
              loading={loading}
            />
          </Grid>
        )}
      </Grid>

      <AppDialog
        open={formOpen}
        onClose={closeForm}
        maxWidth="sm"
        title={editMode ? "Editar sucursal" : "Nueva sucursal"}
        subtitle={
          editMode
            ? "Actualizá los datos de la sucursal."
            : "Completá el formulario para registrar una sucursal."
        }
        icon={<StoreIcon />}
        actions={null}
      >
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1.2,
                  alignItems: "center",
                  mb: 0.5,
                }}
              >
                <Typography sx={{ fontWeight: 800, opacity: 0.85 }}>
                  Datos de la sucursal
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="CUIT/DNI"
                name="identification"
                value={form.identification}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.identification}
                helperText={errors.identification}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Nombre"
                name="name"
                value={form.name}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Alias"
                name="alias"
                value={form.alias}
                onChange={handleChange}
                fullWidth
                error={!!errors.alias}
                helperText={errors.alias}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Responsable"
                name="owner"
                value={form.owner}
                onChange={handleChange}
                fullWidth
                error={!!errors.owner}
                helperText={errors.owner}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Correo"
                name="email"
                value={form.email}
                onChange={handleChange}
                type="email"
                fullWidth
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Teléfono"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                fullWidth
                error={!!errors.phone}
                helperText={errors.phone}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Dirección"
                name="address"
                value={form.address}
                onChange={handleChange}
                fullWidth
                multiline
                minRows={2}
                error={!!errors.address}
                helperText={errors.address}
              />
            </Grid>

            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    onChange={handleToggleActive}
                    color="primary"
                  />
                }
                label="Sucursal activa"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Button
                variant="contained"
                type="submit"
                fullWidth
                endIcon={<SendIcon />}
                disabled={
                  loading || (editMode ? !canUpdateBranch : !canCreateBranch)
                }
                sx={{ py: 1.4, borderRadius: 2, fontWeight: 800 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : editMode ? (
                  "Actualizar"
                ) : (
                  "Registrar"
                )}
              </Button>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Button
                variant="outlined"
                fullWidth
                sx={{ py: 1.2, borderRadius: 2 }}
                onClick={closeForm}
                endIcon={<CancelIcon />}
                disabled={loading}
              >
                Cancelar
              </Button>
            </Grid>
          </Grid>
        </Box>
      </AppDialog>
    </Container>
  );
}
